import {
    Button,
    Card,
    CardContent,
    CardMedia,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import React, { ChangeEvent, DragEvent, useRef, useState } from 'react';
import useApi from '../../api';
import { FileMetadata } from '../../api/types/cms';
import { styled, useTheme } from '@mui/material/styles';
import { useImmer } from 'use-immer';
import {
    CheckOutlined,
    CloseOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../contexts/TranslationContext';
import FileStartEndDate from './FileStartEndDate';

type FileUploadDropZoneProps = {
    value?: FileMetadata | string;
    accept?: string;
    contentName?: string;
    uploadToPool?: string;
    onRefresh?: () => void;
    onError?: () => void;
    onClose?: () => void;
    minHeight?: number | string;
    closeAfterUpload?: boolean;
    allowOverwritingFiles?: boolean;
};

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

type UploadFilesState = {
    file: File;
    result?: FileMetadata | boolean;
    progress: number;
};

export default function FileUploadDropZone({
    value,
    contentName,
    accept = '*',
    uploadToPool = undefined,
    onRefresh = () => {},
    onError = () => {},
    onClose = () => {},
    minHeight = 200,
    closeAfterUpload = false,
    allowOverwritingFiles = true,
}: FileUploadDropZoneProps) {
    const fileRef = useRef<HTMLInputElement>();
    const theme = useTheme();
    const { cms } = useApi();
    const { translate } = useTranslation();

    const [fileDates, setFileDates] = useState<{
        startDate: string;
        endDate: string;
    }>();
    const [uploadProgress, setUploadProgress] = useImmer<
        Array<UploadFilesState>
    >([]);

    const initUpload = (index: number, file: File) => {
        setUploadProgress(draft => {
            draft[index] = { file, progress: 0 };
            return draft;
        });
    };

    const onProgress = (index: number, progress: number) => {
        setUploadProgress(draft => {
            draft[index] = { ...draft[index], progress };
            return draft;
        });
    };

    const successUpload = (index: number, file: FileMetadata) => {
        if (closeAfterUpload) {
            onClose();
        }
        setUploadProgress(draft => {
            draft[index] = { ...draft[index], result: file };
            return draft;
        });
    };

    const errorUpload = (index: number) => {
        setUploadProgress(draft => {
            draft[index] = { ...draft[index], result: false };
            return draft;
        });
    };

    const upload = (
        mode: 'new' | 'update',
        index: number,
        clientFile: File,
    ) => {
        if (mode === 'new') {
            initUpload(index, clientFile);
            cms.uploadFile(
                clientFile,
                uploadToPool,
                contentName,
                onProgress.bind(null, index),
            )
                .then(response => {
                    if (response && !response.error) {
                        successUpload(index, response);
                        onRefresh();
                    }
                })
                .catch((status: { code: number }) => {
                    if (status.code >= 400) {
                        errorUpload(index);
                        onError();
                    }
                });
        }
        if (mode === 'update' && allowOverwritingFiles && value && typeof value !== 'string') {
            initUpload(index, clientFile);
            cms.updateFile(
                value.file_id,
                clientFile,
                fileDates,
                onProgress.bind(null, index),
            )
                .then(response => {
                    if (response && !response.error) {
                        successUpload(index, response);
                        onRefresh();
                    }
                })
                .catch((status: { code: number }) => {
                    if (status.code >= 400) {
                        errorUpload(index);
                        onError();
                    }
                });
        }
    };

    const uploadFromChangeEvent = (event: ChangeEvent) => {
        // @ts-ignore
        const files = event.target.files;
        let index = 0;
        for (const file of files) {
            upload(
                value && allowOverwritingFiles && typeof value !== 'string' ? 'update' : 'new',
                index,
                file,
            );
            index++;
        }
    };

    const uploadFromDrop = (event: DragEvent) => {
        event.preventDefault();
        if (event.dataTransfer.files.length > 0) {
            // @ts-ignore
            const files = event.dataTransfer.files;
            for (let i = 0; i < files.length; i++) {
                const clientFile = files.item(i);
                if (clientFile) {
                    upload(
                        value && allowOverwritingFiles && typeof value !== 'string' ? 'update' : 'new',
                        i,
                        clientFile,
                    );
                }
            }
        }
    };

    const updateMode = value && allowOverwritingFiles && typeof value !== 'string';

    return (
        <Paper
            component="div"
            elevation={4}
            onDrop={uploadFromDrop}
            onDragOver={event => event.preventDefault()}
            sx={{ p: 2 }}>
            <Stack spacing={2} sx={{ pb: 2 }}>
                <Typography
                    variant="body1"
                    color={updateMode ? 'warning.main' : 'info.main'}>
                    {translate(
                        `pages.cms.files.upload_mode_${updateMode ? 'update' : 'new'}`,
                    )}
                </Typography>
                {updateMode && <FileStartEndDate onChange={setFileDates} />}
            </Stack>
            <VisuallyHiddenInput
                type="file"
                onChange={uploadFromChangeEvent}
                accept={accept}
                multiple
                ref={ref => {
                    if (ref) {
                        fileRef.current = ref;
                    }
                }}
            />
            <CardContent
                sx={{
                    minHeight: minHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderWidth: 0.1,
                    borderStyle: 'dashed',
                    borderColor: theme =>
                        theme.palette.getContrastText(
                            theme.palette.background.default,
                        ),
                    borderRadius: 1,
                }}>
                <Stack direction="row" spacing={2}>
                    {uploadProgress.map((progressItem, progressIndex) => (
                        <Card key={`file-${progressIndex}`} variant="outlined">
                            <CardMedia
                                image={URL.createObjectURL(progressItem.file)}
                                sx={{ width: 200, height: 200 }}>
                                <Stack
                                    height="100%"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
                                    {typeof progressItem.result == 'object' ? (
                                        <CheckOutlined
                                            style={{
                                                fontSize: 42,
                                                color: theme.palette.success
                                                    .main,
                                            }}
                                        />
                                    ) : progressItem.result === false ? (
                                        <>
                                            <CloseOutlined
                                                style={{
                                                    fontSize: 42,
                                                    color: theme.palette.error
                                                        .main,
                                                }}
                                            />
                                            <IconButton
                                                onClick={() =>
                                                    upload(
                                                        value &&
                                                            typeof value !==
                                                                'string'
                                                            ? 'update'
                                                            : 'new',
                                                        progressIndex,
                                                        progressItem.file,
                                                    )
                                                }>
                                                <ReloadOutlined />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <CircularProgress
                                            variant="determinate"
                                            value={progressItem.progress * 100}
                                            color="warning"
                                            thickness={4}
                                            size={100}
                                        />
                                    )}
                                </Stack>
                            </CardMedia>
                            <CardContent sx={{ width: 200 }}>
                                {typeof progressItem.result == 'object' && (
                                    <Typography
                                        variant="caption"
                                        title={progressItem.result?.file_id}
                                        fontSize={8}
                                        className="cutTextEnd">
                                        {progressItem.result?.file_id}
                                    </Typography>
                                )}
                                <Typography
                                    variant="subtitle1"
                                    title={progressItem.file.name}
                                    className="cutTextEnd">
                                    {progressItem.file.name}
                                </Typography>
                                <Typography variant="subtitle2">{`${Math.round(progressItem.progress * 100)}%`}</Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
                {uploadProgress.length === 0 && (
                    <Stack alignItems="center" spacing={1}>
                        <Typography variant="subtitle1">
                            Dateien hochladen mit Drag & Drop
                        </Typography>
                        <Typography variant="overline" color="text.secondary">
                            oder
                        </Typography>
                        <Button
                            variant="text"
                            onClick={() => fileRef.current?.click()}>
                            Auswählen
                        </Button>
                    </Stack>
                )}
            </CardContent>
        </Paper>
    );
}
